import React, { useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { Controller } from 'react-hook-form'

const USPS_STATES = [
  { text: 'Armed Forces Americas', value: 'AA' },
  { text: 'Armed Forces Europe', value: 'AE' },
  { text: 'Armed Forces Pacific', value: 'AP' },
  { text: 'Alabama', value: 'AL' },
  { text: 'Alaska', value: 'AK' },
  { text: 'American Samoa', value: 'AS' },
  { text: 'Arizona', value: 'AZ' },
  { text: 'Arkansas', value: 'AR' },
  { text: 'California', value: 'CA' },
  { text: 'Colorado', value: 'CO' },
  { text: 'Connecticut', value: 'CT' },
  { text: 'Delaware', value: 'DE' },
  { text: 'District Of Columbia', value: 'DC' },
  { text: 'Federated States Of Micronesia', value: 'FM' },
  { text: 'Florida', value: 'FL' },
  { text: 'Georgia', value: 'GA' },
  { text: 'Guam', value: 'GU' },
  { text: 'Hawaii', value: 'HI' },
  { text: 'Idaho', value: 'ID' },
  { text: 'Illinois', value: 'IL' },
  { text: 'Indiana', value: 'IN' },
  { text: 'Iowa', value: 'IA' },
  { text: 'Kansas', value: 'KS' },
  { text: 'Kentucky', value: 'KY' },
  { text: 'Louisiana', value: 'LA' },
  { text: 'Maine', value: 'ME' },
  { text: 'Marshall Islands', value: 'MH' },
  { text: 'Maryland', value: 'MD' },
  { text: 'Massachusetts', value: 'MA' },
  { text: 'Michigan', value: 'MI' },
  { text: 'Minnesota', value: 'MN' },
  { text: 'Mississippi', value: 'MS' },
  { text: 'Missouri', value: 'MO' },
  { text: 'Montana', value: 'MT' },
  { text: 'Nebraska', value: 'NE' },
  { text: 'Nevada', value: 'NV' },
  { text: 'New Hampshire', value: 'NH' },
  { text: 'New Jersey', value: 'NJ' },
  { text: 'New Mexico', value: 'NM' },
  { text: 'New York', value: 'NY' },
  { text: 'North Carolina', value: 'NC' },
  { text: 'North Dakota', value: 'ND' },
  { text: 'Northern Mariana Islands', value: 'MP' },
  { text: 'Ohio', value: 'OH' },
  { text: 'Oklahoma', value: 'OK' },
  { text: 'Oregon', value: 'OR' },
  { text: 'Palau', value: 'PW' },
  { text: 'Pennsylvania', value: 'PA' },
  { text: 'Puerto Rico', value: 'PR' },
  { text: 'Rhode Island', value: 'RI' },
  { text: 'South Carolina', value: 'SC' },
  { text: 'South Dakota', value: 'SD' },
  { text: 'Tennessee', value: 'TN' },
  { text: 'Texas', value: 'TX' },
  { text: 'Utah', value: 'UT' },
  { text: 'Vermont', value: 'VT' },
  { text: 'U.S. Virgin Islands', value: 'VI' },
  { text: 'Virginia', value: 'VA' },
  { text: 'Washington', value: 'WA' },
  { text: 'West Virginia', value: 'WV' },
  { text: 'Wisconsin', value: 'WI' },
  { text: 'Wyoming', value: 'WY' },
]

const normalizeText = text =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .trim()

const createOptions = () =>
  USPS_STATES.map(option => ({
    ...option,
    label: `${option.text} (${option.value})`,
    normalizedText: normalizeText(option.text),
    normalizedValue: normalizeText(option.value),
  }))

const levenshteinDistance = (a, b) => {
  const normalizedA = normalizeText(a)
  const normalizedB = normalizeText(b)
  const rows = normalizedB.length + 1
  const cols = normalizedA.length + 1
  const matrix = Array.from({ length: rows }, () => new Array(cols).fill(0))

  for (let i = 0; i < cols; i += 1) {
    matrix[0][i] = i
  }
  for (let j = 0; j < rows; j += 1) {
    matrix[j][0] = j
  }

  for (let j = 1; j < rows; j += 1) {
    for (let i = 1; i < cols; i += 1) {
      matrix[j][i] = Math.min(
        matrix[j - 1][i] + 1,
        matrix[j][i - 1] + 1,
        matrix[j - 1][i - 1] + (normalizedA[i - 1] === normalizedB[j - 1] ? 0 : 1),
      )
    }
  }

  return matrix[rows - 1][cols - 1]
}

const getMatchScore = (input, option) => {
  const query = normalizeText(input)
  if (!query) return 0

  if (option.normalizedValue === query) return 100
  if (option.normalizedText === query) return 100
  if (option.normalizedText.startsWith(query)) return 95
  if (option.normalizedValue.startsWith(query)) return 90
  if (option.normalizedText.includes(query)) return 80
  if (option.normalizedValue.includes(query)) return 75

  const distanceText = levenshteinDistance(query, option.normalizedText)
  if (distanceText <= 2) return 70 - distanceText * 5

  const distanceValue = levenshteinDistance(query, option.normalizedValue)
  if (distanceValue <= 1) return 65 - distanceValue * 10

  return 0
}

const getOptionByValue = (options, value) =>
  options.find(option => normalizeText(option.value) === normalizeText(value))

const resolveOption = (value, options) => {
  const query = normalizeText(value)
  if (!query) return null

  const exact = options.find(
    option =>
      option.normalizedValue === query || option.normalizedText === query,
  )
  if (exact) {
    return exact
  }

  const candidates = options
    .map(option => ({ option, score: getMatchScore(query, option) }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)

  return candidates.length ? candidates[0].option : null
}

const getErrorText = (error, touched, internalError) => {
  if (!touched) return ''
  if (internalError) return internalError
  if (!error) return ''
  return typeof error === 'string' ? error : error.message
}

export const StateAutocomplete = ({
  name,
  control,
  form,
  field,
  label = 'State',
  error,
  touched,
  required = false,
  disabled = false,
  placeholder = 'Type a state name or abbreviation',
  defaultValue = '',
  fullWidth = true,
}) => {
  const isFormik = Boolean(field)
  const fieldName = isFormik ? field.name : name
  const options = useMemo(createOptions, [])
  const [inputValue, setInputValue] = useState('')
  const [internalError, setInternalError] = useState('')
  const currentValue = isFormik ? field.value : form?.values?.[fieldName]
  const touchedState = touched || (isFormik ? Boolean(form?.touched?.[fieldName]) : false)

  useEffect(() => {
    if (!control) {
      const activeOption = getOptionByValue(options, currentValue)
      setInputValue(activeOption ? activeOption.label : currentValue || '')
    }
  }, [control, currentValue, options])

  const clearError = () => {
    setInternalError('')
    if (form?.setFieldError) {
      form.setFieldError(fieldName, undefined)
    }
  }

  const setFormValue = value => {
    if (isFormik && field?.onChange) {
      field.onChange({ target: { name: fieldName, value } })
    } else if (form?.setFieldValue) {
      form.setFieldValue(fieldName, value)
    }
  }

  const setFormError = message => {
    if (form?.setFieldError) {
      form.setFieldError(fieldName, message)
    }
  }

  const handleResolvedSelection = (resolved, onChange) => {
    if (!resolved) {
      setInternalError('Please select a valid state')
      onChange('')
      setFormError('Please select a valid state')
      return
    }

    clearError()
    setInputValue(resolved.label)
    onChange(resolved.value)
  }

  const handleSelection = (newValue, onChange) => {
    if (!newValue) {
      setInputValue('')
      clearError()
      onChange('')
      return
    }

    if (typeof newValue === 'string') {
      const resolved = resolveOption(newValue, options)
      handleResolvedSelection(resolved, onChange)
      return
    }

    setInputValue(newValue.label)
    clearError()
    onChange(newValue.value)
  }

  const handleBlur = (event, onChange) => {
    const resolved = resolveOption(inputValue, options)
    if (resolved) {
      handleResolvedSelection(resolved, onChange)
      return
    }

    if (!inputValue) {
      clearError()
      onChange('')
      return
    }

    handleResolvedSelection(null, onChange)
  }

  const helperText = getErrorText(error, touchedState, internalError)

  const getCurrentValue = () =>
    isFormik ? currentValue : form?.values?.[fieldName]

  const handleClear = onChange => {
    setInputValue('')
    clearError()
    onChange('')
    setFormValue('')
  }

  const autocompleteSx = {
    width: '100%',
    '& .MuiAutocomplete-inputRoot': {
      width: '100%',
      minWidth: '30px',
      padding: '0px',
    },
    '& .MuiAutocomplete-input': {
      opacity: 1,
      width: '100% !important',
      minWidth: '30px',
      padding: '16.5px 14px',
    },
    '& .MuiOutlinedInput-root': {
      width: '100%',
      padding: '0px',
      minHeight: '66px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '16.5px 14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(50, 45, 43)',
    },
    '& .MuiInputLabel-asterisk': {
      color: 'red',
    },
  }

  if (control) {
    return (
      <FormControl fullWidth={fullWidth}>
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              freeSolo
              sx={autocompleteSx}
              options={options}
              value={getOptionByValue(options, value) || null}
              inputValue={inputValue}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'clear') {
                  handleClear(onChange)
                  return
                }
                setInputValue(newInputValue)
              }}
              onChange={(event, newValue) => handleSelection(newValue, onChange)}
              filterOptions={(optionsToFilter, state) =>
                state.inputValue
                  ? optionsToFilter
                      .map(option => ({
                        option,
                        score: getMatchScore(state.inputValue, option),
                      }))
                      .filter(item => item.score > 0)
                      .sort((a, b) => b.score - a.score)
                      .map(item => item.option)
                  : optionsToFilter
              }
              getOptionLabel={option => option.label || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  variant="outlined"
                  required={required}
                  disabled={disabled}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true,
                  }}
                  onBlur={event => {
                    params.inputProps.onBlur?.(event)
                    handleBlur(event, onChange)
                  }}
                  error={Boolean((error && touchedState) || internalError)}
                  helperText={helperText}
                  fullWidth
                />
              )}
              noOptionsText="No matching state"
            />
          )}
        />
        {error && touchedState && !internalError && (
          <FormHelperText error>
            {typeof error === 'string' ? error : error.message}
          </FormHelperText>
        )}
      </FormControl>
    )
  }

  return (
    <FormControl fullWidth={fullWidth}>
      <Autocomplete
        freeSolo
        sx={autocompleteSx}
        options={options}
        value={getOptionByValue(options, getCurrentValue()) || null}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'clear') {
            handleClear(value => setFormValue(value))
            return
          }
          setInputValue(newInputValue)
        }}
        onChange={(event, newValue) =>
          handleSelection(newValue, value => setFormValue(value))
        }
        filterOptions={(optionsToFilter, state) =>
          state.inputValue
            ? optionsToFilter
                .map(option => ({
                  option,
                  score: getMatchScore(state.inputValue, option),
                }))
                .filter(item => item.score > 0)
                .sort((a, b) => b.score - a.score)
                .map(item => item.option)
            : optionsToFilter
        }
        getOptionLabel={option => option.label || ''}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            required={required}
            disabled={disabled}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: true,
            }}
            onBlur={event => handleBlur(event, value => form?.setFieldValue?.(fieldName, value))}
            error={Boolean((error && touchedState) || internalError)}
            helperText={helperText}
            fullWidth
          />
        )}
        noOptionsText="No matching state"
      />
      {error && touchedState && !internalError && (
        <FormHelperText error>
          {typeof error === 'string' ? error : error.message}
        </FormHelperText>
      )}
    </FormControl>
  )
}
